import React, { useState } from 'react';
import { WD_LONG, ordinal } from '../../lib/quickAdd';
import { recurrenceText } from '../../lib/meetings';
import { Icon } from '../todos/icons';

/*
 * HOW OFTEN A MEETING REPEATS (Gordon, 22 October: "recurring meetings").
 *
 * The to-do editor's Repeat picker, in the shape an invitation uses: none,
 * every day, every week on these days, every month on this day - and
 * "Custom…" for every N. It ends never, on a date, or after a number of
 * times, because an invitation can say "6 times" where a to-do cannot. The
 * value is the contract's recurrence object and nothing else:
 *
 *   { freq: 'daily'|'weekly'|'monthly', interval, weekdays?, monthday?, until?, count? }
 *
 * `anchor` is the meeting's date ('YYYY-MM-DD'), which names the presets -
 * "Weekly on Thursday" when the meeting is on a Thursday.
 */

const WEEK = [1, 2, 3, 4, 5, 6, 0];
const UNITS = [
  { freq: 'daily', one: 'day', many: 'days' },
  { freq: 'weekly', one: 'week', many: 'weeks' },
  { freq: 'monthly', one: 'month', many: 'months' }
];

/** Only the keys that mean something, in one order, so an echo does not read as a change. */
export function cleanRecurrence(r) {
  if (!r || !r.freq) return null;
  const out = { freq: r.freq, interval: Math.max(1, Number(r.interval) || 1) };
  if (r.freq === 'weekly' && Array.isArray(r.weekdays) && r.weekdays.length) {
    out.weekdays = [...new Set(r.weekdays.map(Number))].sort((a, b) => a - b);
  }
  if (r.freq === 'monthly' && r.monthday) out.monthday = Number(r.monthday);
  if (r.until) out.until = String(r.until).slice(0, 10);
  else if (r.count) out.count = Math.max(1, Number(r.count) || 1);
  return out;
}

const dayOf = (anchor) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(anchor || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date();
};

const presets = (anchor) => {
  const d = dayOf(anchor);
  return [
    { key: 'daily', label: 'Every day', rec: { freq: 'daily', interval: 1 } },
    { key: 'weekdays', label: 'Every weekday (Mon–Fri)', rec: { freq: 'weekly', interval: 1, weekdays: [1, 2, 3, 4, 5] } },
    { key: 'weekly', label: `Weekly on ${WD_LONG[d.getDay()]}`, rec: { freq: 'weekly', interval: 1, weekdays: [d.getDay()] } },
    { key: 'fortnightly', label: `Every 2 weeks on ${WD_LONG[d.getDay()]}`, rec: { freq: 'weekly', interval: 2, weekdays: [d.getDay()] } },
    { key: 'monthly', label: `Monthly on the ${ordinal(d.getDate())}`, rec: { freq: 'monthly', interval: 1, monthday: d.getDate() } }
  ];
};

const sameShape = (a, b) => {
  const x = cleanRecurrence(a); const y = cleanRecurrence(b);
  if (!x || !y) return false;
  return x.freq === y.freq && x.interval === y.interval
    && JSON.stringify(x.weekdays || null) === JSON.stringify(y.weekdays || null)
    && (x.monthday || null) === (y.monthday || null);
};

export default function RecurrencePicker({ value, onChange, anchor, idPrefix = 'mt-rec' }) {
  const list = presets(anchor);
  const cur = cleanRecurrence(value);
  const hit = cur ? list.find((p) => sameShape(p.rec, cur)) : null;
  const [custom, setCustom] = useState(Boolean(cur) && !hit);
  const choice = !cur ? '' : (hit && !custom ? hit.key : 'custom');
  const set = (patch) => onChange(cleanRecurrence({ ...(cur || { freq: 'weekly', interval: 1 }), ...patch }));

  const pick = (key) => {
    if (key === '') { setCustom(false); onChange(null); return; }
    if (key === 'custom') {
      setCustom(true);
      if (!cur) onChange(cleanRecurrence({ freq: 'weekly', interval: 1, weekdays: [dayOf(anchor).getDay()] }));
      return;
    }
    setCustom(false);
    // Keep how it ends when only the rhythm changes.
    const ends = cur ? { until: cur.until, count: cur.count } : {};
    onChange(cleanRecurrence({ ...list.find((p) => p.key === key).rec, ...ends }));
  };

  const unit = UNITS.find((u) => u.freq === cur?.freq) || UNITS[1];
  const n = cur ? cur.interval : 1;
  const days = new Set(cur?.weekdays || []);
  const ends = !cur ? 'never' : cur.until ? 'until' : cur.count ? 'count' : 'never';

  return (
    <div className="mt-rec" data-recurrence={cur ? cur.freq : 'none'}>
      <label className="tds-field">
        <span>Repeats</span>
        <select id={`${idPrefix}-freq`} aria-label="Repeats" value={choice} onChange={(e) => pick(e.target.value)}>
          <option value="">Does not repeat</option>
          {list.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          <option value="custom">Custom…</option>
        </select>
      </label>

      {choice === 'custom' && cur && (
        <div className="mt-rec-custom">
          <div className="mt-rec-every">
            <span>Every</span>
            <input type="number" min={1} max={99} inputMode="numeric" aria-label="Repeat every" value={n}
              onChange={(e) => set({ interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })} />
            <select aria-label="Repeat unit" value={unit.freq}
              onChange={(e) => set({ freq: e.target.value, weekdays: undefined, monthday: undefined })}>
              {UNITS.map((u) => <option key={u.freq} value={u.freq}>{n === 1 ? u.one : u.many}</option>)}
            </select>
          </div>
          {unit.freq === 'weekly' && (
            <div className="mt-rec-days" role="group" aria-label="On these days">
              {WEEK.map((d) => (
                <button key={d} type="button" className={`mt-rec-day${days.has(d) ? ' is-on' : ''}`}
                  aria-pressed={days.has(d)} aria-label={`Repeat on ${WD_LONG[d]}`}
                  onClick={() => {
                    const next = new Set(days);
                    if (next.has(d)) next.delete(d); else next.add(d);
                    set({ weekdays: [...next] });
                  }}>
                  {WD_LONG[d].slice(0, 2)}
                </button>
              ))}
            </div>
          )}
          {unit.freq === 'monthly' && (
            <label className="tds-field">
              <span>On</span>
              <select aria-label="Day of the month" value={cur.monthday ? String(cur.monthday) : ''}
                onChange={(e) => set({ monthday: e.target.value ? Number(e.target.value) : undefined })}>
                <option value="">the meeting&rsquo;s day</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={String(d)}>the {ordinal(d)}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {cur && (
        <div className="mt-rec-ends" role="group" aria-label="Ends">
          <span className="mt-rec-ends-label">Ends</span>
          <label className="mt-rec-end">
            <input type="radio" name={`${idPrefix}-ends`} value="never" checked={ends === 'never'}
              onChange={() => set({ until: undefined, count: undefined })} />
            <span>Never</span>
          </label>
          <label className="mt-rec-end">
            <input type="radio" name={`${idPrefix}-ends`} value="until" checked={ends === 'until'}
              onChange={() => set({ count: undefined, until: cur.until || anchor || new Date().toISOString().slice(0, 10) })} />
            <span>On</span>
            <input type="date" aria-label="Repeat until" value={cur.until || ''} disabled={ends !== 'until'}
              min={anchor || undefined} onChange={(e) => set({ until: e.target.value || undefined, count: undefined })} />
          </label>
          <label className="mt-rec-end">
            <input type="radio" name={`${idPrefix}-ends`} value="count" checked={ends === 'count'}
              onChange={() => set({ until: undefined, count: cur.count || 6 })} />
            <span>After</span>
            <input type="number" min={1} max={365} inputMode="numeric" aria-label="Number of times"
              value={cur.count || ''} disabled={ends !== 'count'}
              onChange={(e) => set({ count: Math.max(1, Math.min(365, Number(e.target.value) || 1)), until: undefined })} />
            <span>times</span>
          </label>
        </div>
      )}

      {cur && (
        <p className="mt-rec-summary" data-recurrence-summary="">
          <Icon name="repeat" size={13} /> {recurrenceText(cur)}
        </p>
      )}
    </div>
  );
}
